import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Quote } from 'lucide-react';

/**
 * ✍️ EDIT THESE QUOTES — replace with real partner testimonials when collected.
 * Format: { quote: string, name: string, role: string, org: string, initials: string }
 */
const TESTIMONIALS = [
  {
    quote:
      "ServeNetics doesn't just build software — they build operating systems for institutions. We went from spreadsheet chaos to a working AI tutor pilot in eight weeks. That's the kind of speed our market needs.",
    name: 'Head of Operations',
    role: 'Operations Lead',
    org: 'EdTech Pilot Partner',
    initials: 'HO',
  },
  {
    quote:
      "Working with Chuks felt less like hiring a vendor and more like onboarding a fractional CTO who already understood our terrain. The implementation discipline is what stood out — every milestone, on time.",
    name: 'School Administrator',
    role: 'Administrator',
    org: 'Lagos-based Learning Network',
    initials: 'SA',
  },
];

export function TestimonialSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="testimonial-section"
      aria-label="Partner testimonials"
    >
      {/* Subtle background flare */}
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-signal-glow rounded-full blur-[160px] -translate-y-1/2 opacity-40 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14 max-w-3xl"
        >
          <p className="eyebrow section-eyebrow-line mb-6">From The Field</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-stark tracking-tight">
            What Partners <span className="text-gradient">Tell Us</span>
          </h2>
        </motion.div>

        {/* Quote grid */}
        <div className="grid md:grid-cols-2 gap-px bg-line-1 border border-line-1 rounded-md overflow-hidden">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={t.org}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="bg-surface hover:bg-surface-2 transition-colors duration-300 p-8 md:p-10 relative flex flex-col"
              data-testid={`testimonial-${i}`}
            >
              {/* Decorative quote glyph */}
              <Quote
                className="absolute top-6 right-6 w-8 h-8 text-stark/[0.06] -scale-x-100"
                aria-hidden="true"
              />

              <span className="eyebrow text-signal mb-5">QUOTE.0{i + 1}</span>

              <blockquote className="text-lg md:text-xl text-stark leading-relaxed mb-8 font-light flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>

              <figcaption className="flex items-center gap-4 pt-6 border-t border-line-1">
                <div
                  className="w-11 h-11 rounded-md bg-ink-2 border border-line-1 flex items-center justify-center font-mono text-sm text-stark-2 flex-shrink-0"
                  aria-hidden="true"
                >
                  {t.initials}
                </div>
                <div>
                  <p className="font-heading font-semibold text-stark text-sm tracking-tight">
                    {t.name}
                  </p>
                  <p className="text-xs text-stark-2 font-mono uppercase tracking-wider">
                    {t.org}
                  </p>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export default TestimonialSection;
